import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../utils/httpClient';
import type { TMdmPolicyTemplates, TMdmDevice } from "@nucleus-care/nucleuscare-data-types";

export const fetchMdmDevicePolicies = createAsyncThunk<TMdmPolicyTemplates[], string>(
  'mdmDevice/data/fetchMdmDevicePolicies',
  async (mdmDeviceId: string) => {
    const { data } = await api.get(`/mdm/devices/${mdmDeviceId}/policies`)
    return data.DevicePolicies
  }
);

export const assignPolicyTemplatesToMdmDevice = createAsyncThunk<string[], { mdmDeviceId: string, MdmPolicyTemplates: TMdmPolicyTemplates[] }>(
  'mdmDevice/data/assignPolicyTemplatesToMdmDevice',
  async ({ mdmDeviceId, MdmPolicyTemplates }: { mdmDeviceId: string, MdmPolicyTemplates: TMdmPolicyTemplates[] }) => {
    const { data } = await api.post(`mdm/devices/${mdmDeviceId}/policy_templates`, { MdmPolicyTemplates })
    return data.mdmPolicyTemplates as string[]
  }
);

export const unassignPolicyTemplateFromMdmDevice = createAsyncThunk<string[], { devicePolicyTemplatesId: string }>(
  'mdmDevice/data/unassignPolicyTemplateFromMdmDevice',
  async ({ devicePolicyTemplatesId }: { devicePolicyTemplatesId: string }) => {
    const { data } = await api.delete(`mdm/device_policy_template/${devicePolicyTemplatesId}`)
    return data.mdmPolicyTemplates as string[]
  }
);

export const updateMdmDeviceData = createAsyncThunk<string[], { mdmDeviceId: string, mdmDeviceData: any }>(
  'mdmDevice/data/updateMdmDeviceData',
  async ({ mdmDeviceId, mdmDeviceData }: { mdmDeviceId: string, mdmDeviceData: any }) => {
    const { data } = await api.put(`/mdm/devices/${mdmDeviceId}`, { mdmDeviceData })
    return data
  }
);

export const refreshMdmDevicePolicy = createAsyncThunk<string[], { mdmDeviceId: string }>(
  'mdmDevice/data/refreshMdmDevicePolicy',
  async ({ mdmDeviceId }: { mdmDeviceId: string }) => {
    const { data } = await api.put(`/mdm/device/${mdmDeviceId}/refresh_policy`, {})
    return data
  }
);

type IDeleteMdmDevicesParams = { unassign: boolean, disenrollMdm: boolean, disenrollHexnode: boolean, removeFromSystem: boolean, devices: { CareDeviceID: string } }
type DeviceToDelete = { MdmDeviceID?: string, CareDeviceID?: string }
type DeleteMdmDeviceResponseType = {
  failedResults: DeviceToDelete[]
  results: {
    device: DeviceToDelete;
    errors: string[];
  }[];
  status: string;
  ok: boolean;
  v: number;
};
export const deleteMdmDevice = createAsyncThunk<DeleteMdmDeviceResponseType, IDeleteMdmDevicesParams>(
  'mdmDevice/data/deleteMdmDevice',
  async ({ unassign, removeFromSystem, devices, disenrollMdm, disenrollHexnode }: IDeleteMdmDevicesParams) => {
    const { data } = await api.post(`/device/delete/batch`, {
      unassign, removeFromSystem,
      disenrollMdm,
      disenrollHexnode,
      devices
    }) as {
      data: DeleteMdmDeviceResponseType
    }
    return data
  }
)

type IFetchDeviceListParams = {
  offset: number;
  limit: number;
  searchText?: string;
  mdmAccountId?: string;
  fullData?: boolean;
  _sort?: string
}
export const fetchMdmDevicesList = createAsyncThunk<{ results: TMdmDevice[], count: number, containCareDevices: boolean }, IFetchDeviceListParams>(
  'mdmDevice/data/fetchMdmDevicesList',
  async ({ offset = 0, limit = 10, searchText = "", mdmAccountId = "", fullData = false, _sort }: IFetchDeviceListParams) => {
    const encodedSearchText = encodeURIComponent(searchText);
    const encodedMdmAccountId = encodeURIComponent(mdmAccountId);
    let extraQueryParams = ''
    if (searchText && searchText != '') {
      extraQueryParams += '&searchText=' + encodedSearchText
    }
    if (mdmAccountId && mdmAccountId != '') {
      extraQueryParams += '&mdmAccountId=' + encodedMdmAccountId
    }
    if (fullData) {
      extraQueryParams += '&fullData=true'
    }
    if (_sort) {
      extraQueryParams += `&_sort=${_sort}`
    }
    const { data } = await api.get(`/mdm/devices?offset=${offset}&limit=${limit}${extraQueryParams}`);
    return { results: data.results, count: data.count, containCareDevices: data.containCareDevices };
  }
);

export const fetchMdmDeviceDetails = createAsyncThunk<TMdmDevice, string>(
  'mdmDevice/data/fetchMdmDeviceDetails',
  async (mdmDeviceId: string) => {
    const { data } = await api.get(`/mdm/devices/${mdmDeviceId}`);
    return data.Device;
  }
);

type TMdmDeviceData = Partial<TMdmDevice> & {
    SerialNumber?: string;

    MdmID?: string;
    MdmName?: string;
    MdmDeviceInfo?: string|object;
    MdmDevicePolicyJSON?: string|object;
    CareDeviceID?: string | null;
    CareAccountName?:string;
    CarePatientName?:string;
    CareStatus?: boolean;
};

const initialStateValues = {
  fetchDeviceList: {
    loading: false,
    success: false,
    error: false,
    data_count: 0,
    containCareDevices: false,
    data: [] as TMdmDevice[]
  },
  fetchDeviceDetails: {
    loading: false,
    success: false,
    error: false,
    deviceDetails: {} as TMdmDeviceData
  },
  fetchDevicePolicies: {
    loading: false,
    success: false,
    error: false,
    devicePolicies: [] as TMdmPolicyTemplates[]
  },
  assignPolicyTemplatesToMdmDeviceState: {
    loading: false,
    success: false,
    error: false,
    mdmPolicyTemplates: [] as string[]
  },
  unassignPolicyTemplateFromMdmDeviceState: {
    loading: false,
    success: false,
    error: false,
    mdmPolicyTemplates: [] as string[]
  },
  updateMdmDeviceData: {
    loading: false,
    success: false,
    error: false,
  },
  refreshMdmDevicePolicy: {
    loading: false,
    success: false,
    error: false,
  },
  deleteMdmDevice: {
    data: {} as DeleteMdmDeviceResponseType,
    loading: false,
    success: false,
    error: false,
  }
}

const mdmDeviceDataSlice = createSlice({
  name: "mdmDevice/data",
  initialState: initialStateValues,
  reducers: {
    resetFetchDeviceList: (state) => {
      state.fetchDeviceList = initialStateValues.fetchDeviceList
    },
    resetFetchDeviceDetails: (state) => {
      state.fetchDeviceDetails = initialStateValues.fetchDeviceDetails
    },
    resetFetchDevicePoliciesState: (state) => {
      state.fetchDevicePolicies = initialStateValues.fetchDevicePolicies
    },
    resetAssignPolicyTemplatesToMdmDeviceState: (state) => {
      state.assignPolicyTemplatesToMdmDeviceState = initialStateValues.assignPolicyTemplatesToMdmDeviceState
    },
    resetUnassignPolicyTemplateFromMdmDeviceState: (state) => {
      state.unassignPolicyTemplateFromMdmDeviceState = initialStateValues.unassignPolicyTemplateFromMdmDeviceState
    },
    resetDeleteMdmDeviceState: (state) => {
      state.deleteMdmDevice = initialStateValues.deleteMdmDevice
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMdmDevicesList.pending, (state) => {
      state.fetchDeviceList = {
        loading: true,
        success: false,
        error: false,
        data_count: state.fetchDeviceList.data_count,
        containCareDevices: state.fetchDeviceList.containCareDevices || false,
        data: state.fetchDeviceList.data,
      }
    })
      .addCase(fetchMdmDevicesList.fulfilled, (state, action) => {
        state.fetchDeviceList = {
          loading: false,
          success: true,
          error: false,
          data_count: action.payload.count,
          containCareDevices: action.payload.containCareDevices || false,
          data: action.payload.results
        }
      })

      .addCase(fetchMdmDevicesList.rejected, (state) => {
        state.fetchDeviceList = {
          loading: false,
          success: false,
          error: true,
          data_count: state.fetchDeviceList.data_count,
          containCareDevices: state.fetchDeviceList.containCareDevices || false,
          data: state.fetchDeviceList.data,
        }
      })
    builder
      .addCase(fetchMdmDevicePolicies.pending, (state) => {
        state.fetchDevicePolicies = {
          loading: true,
          success: false,
          error: false,
          devicePolicies: [],
        }
      })
      .addCase(fetchMdmDevicePolicies.fulfilled, (state, action) => {
        state.fetchDevicePolicies = {
          loading: false,
          success: true,
          error: false,
          devicePolicies: action.payload,
        }
      })
      .addCase(fetchMdmDevicePolicies.rejected, (state) => {
        state.fetchDevicePolicies = {
          loading: false,
          success: true,
          error: false,
          devicePolicies: [],
        }
      })
    builder
      .addCase(fetchMdmDeviceDetails.pending, (state) => {
        state.fetchDeviceDetails = {
          loading: true,
          success: false,
          error: false,
          deviceDetails: {},
        }
      })
      .addCase(fetchMdmDeviceDetails.fulfilled, (state, action) => {
        state.fetchDeviceDetails = {
          loading: false,
          success: true,
          error: false,
          deviceDetails: action.payload,
        }
      })
      .addCase(fetchMdmDeviceDetails.rejected, (state) => {
        state.fetchDeviceDetails = {
          loading: false,
          success: true,
          error: false,
          deviceDetails: {},
        }
      })
    builder
      .addCase(assignPolicyTemplatesToMdmDevice.pending, (state) => {
        state.assignPolicyTemplatesToMdmDeviceState = {
          loading: true,
          success: false,
          error: false,
          mdmPolicyTemplates: [],
        }
      })
      .addCase(assignPolicyTemplatesToMdmDevice.fulfilled, (state, action) => {
        state.assignPolicyTemplatesToMdmDeviceState = {
          loading: false,
          success: true,
          error: false,
          mdmPolicyTemplates: action.payload,
        }
      })
      .addCase(assignPolicyTemplatesToMdmDevice.rejected, (state) => {
        state.assignPolicyTemplatesToMdmDeviceState = {
          loading: false,
          success: false,
          error: true,
          mdmPolicyTemplates: [],
        }
      })
    builder
      .addCase(unassignPolicyTemplateFromMdmDevice.pending, (state) => {
        state.unassignPolicyTemplateFromMdmDeviceState = {
          loading: true,
          success: false,
          error: false,
          mdmPolicyTemplates: [],
        }
      })
      .addCase(unassignPolicyTemplateFromMdmDevice.fulfilled, (state, action) => {
        state.unassignPolicyTemplateFromMdmDeviceState = {
          loading: false,
          success: true,
          error: false,
          mdmPolicyTemplates: action.payload,
        }
      })
      .addCase(unassignPolicyTemplateFromMdmDevice.rejected, (state) => {
        state.unassignPolicyTemplateFromMdmDeviceState = {
          loading: false,
          success: false,
          error: true,
          mdmPolicyTemplates: [],
        }
      })
    builder
      .addCase(updateMdmDeviceData.pending, (state) => {
        state.updateMdmDeviceData = {
          loading: true,
          success: false,
          error: false,
        }
      })
      .addCase(updateMdmDeviceData.fulfilled, (state) => {
        state.updateMdmDeviceData = {
          loading: false,
          success: true,
          error: false,
        }
      })
      .addCase(updateMdmDeviceData.rejected, (state) => {
        state.updateMdmDeviceData = {
          loading: false,
          success: false,
          error: true,
        }
      })

    builder
      .addCase(deleteMdmDevice.pending, (state) => {
        state.deleteMdmDevice = {
          data: initialStateValues.deleteMdmDevice.data,
          loading: true,
          success: false,
          error: false,
        }
      })
      .addCase(deleteMdmDevice.fulfilled, (state, action) => {
        state.deleteMdmDevice = {
          data: action.payload,
          loading: false,
          success: true,
          error: false,
        }
      })
      .addCase(deleteMdmDevice.rejected, (state) => {
        state.deleteMdmDevice = {
          data: {
            failedResults: [],
            results: [],
            status: 'failed',
            ok: false,
            v: 0,
          },
          loading: false,
          success: false,
          error: true,
        }
      })
  }
});

export const {
  resetFetchDeviceList,
  resetFetchDeviceDetails,
  resetFetchDevicePoliciesState,
  resetAssignPolicyTemplatesToMdmDeviceState,
  resetUnassignPolicyTemplateFromMdmDeviceState,
  resetDeleteMdmDeviceState
} = mdmDeviceDataSlice.actions;

export default mdmDeviceDataSlice.reducer;
